import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import RichEditor from "@/components/letters/RichEditor";
import LetterInfoForm from "@/components/letters/LetterInfoForm";
import AiPanel from "@/components/letters/AiPanel";
import Attachments from "@/components/letters/Attachments";
import { TemplatePicker } from "@/components/letters/Pickers";
import { PreviewDialog } from "@/components/letters/Dialogs";
import { useLetterOutput } from "@/components/letters/useLetterOutput";
import { emptyLetter, fillVars, lcall, letterApi, VARIABLES, STATUS_STYLE } from "@/lib/letters";
import { Save, FileEdit, Eye, Sparkles, FileDown, Printer, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = ["date", "type", "objet", "service", "destinataire", "destinataire_fonction", "entreprise", "adresse", "email", "telephone", "expediteur",
  "expediteur_fonction", "ref_precedente", "lieu", "signataire_id", "avec_cachet", "contenu_html", "recipient_link", "template_id"];

function ActionBar({ onSave, onDraft, onPreview, onAi, onPdf, onPrint, onValidate, saving, busy, canValidate, statut, reference }) {
  const B = ({ on, icon: I, label, testid, primary }) => (
    <Button size="sm" variant={primary ? "default" : "outline"} className={primary ? "bg-brand-600 hover:bg-brand-700 text-white" : "bg-white"} onClick={on} disabled={saving || busy} data-testid={testid}>
      <I className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{label}</span>
    </Button>
  );
  return (
    <div className="sticky top-0 z-20 -mx-4 sm:mx-0 mb-4 flex flex-wrap items-center gap-1.5 border-b sm:border sm:rounded-xl bg-white/95 backdrop-blur px-3 py-2 shadow-sm" data-testid="letter-actionbar">
      <div className="mr-auto min-w-0">
        <p className="font-head font-bold truncate">{reference || "Nouveau courrier"}</p>
        {statut && <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${STATUS_STYLE[statut]}`} data-testid="letter-status">{statut}</span>}
      </div>
      <B on={onSave} icon={saving ? Loader2 : Save} label="Enregistrer" testid="act-save" primary />
      <B on={onDraft} icon={FileEdit} label="Brouillon" testid="act-draft" />
      <B on={onPreview} icon={busy ? Loader2 : Eye} label="Aperçu" testid="act-preview" />
      <B on={onAi} icon={Sparkles} label="✨ Assistant IA" testid="act-ai" />
      <B on={onPdf} icon={FileDown} label="Générer PDF" testid="act-pdf" />
      <B on={onPrint} icon={Printer} label="Imprimer" testid="act-print" />
      <B on={onValidate} icon={CheckCircle2} label={canValidate ? "Valider" : "Soumettre"} testid="act-validate" />
    </div>
  );
}

export default function LetterEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [company, setCompany] = useState(null);
  const [l, setL] = useState(null);
  const [refPreview, setRefPreview] = useState("");
  const [editor, setEditor] = useState(null);
  const [sel, setSel] = useState(null);
  const [pages, setPages] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const aiRef = useRef(null);
  useEffect(() => {
    Promise.all([letterApi.config(), letterApi.company(), id ? api.get(`/letters/${id}`).then((r) => r.data) : Promise.resolve(emptyLetter(user))])
      .then(([c, co, d]) => {
        if (d.statut && !["BROUILLON", "À VALIDER"].includes(d.statut)) { nav(`/courriers/${d.id}`, { replace: true }); return; }
        setCfg(c); setCompany(co); setL(d);
      }).catch(() => { toast.error("Courrier introuvable"); nav("/courriers"); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (l && !l.id) api.get(`/letters/next-number?service=${l.service || ""}`).then((r) => setRefPreview(r.data.reference)); }, [l?.service, l?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const output = useLetterOutput({ letter: l && { ...l, reference: l.reference || refPreview }, cfg, company });
  if (!l || !cfg) return <Loader />;
  const perms = cfg.perms || [];
  const set = (p) => setL((x) => ({ ...x, ...p }));
  const save = async (statut, quiet) => {
    const body = FIELDS.reduce((o, k) => ({ ...o, [k]: l[k] }), { statut });
    setSaving(true);
    const r = await lcall(() => (l.id ? api.put(`/letters/${l.id}`, body) : api.post("/letters", body)), quiet ? null : statut === "BROUILLON" ? "Enregistré comme brouillon" : "Courrier enregistré");
    setSaving(false);
    if (!r) return null;
    setL(r);
    if (!l.id) nav(`/courriers/${r.id}/modifier`, { replace: true });
    return r;
  };
  const validate = async () => {
    const r = await save(undefined, true);
    if (!r) return;
    const sig = (cfg.signatories || []).find((s) => s.id === r.signataire_id);
    if (sig?.user_id) {
      if (r.statut === "À VALIDER" && r.pending_signer_id) { nav(`/courriers/${r.id}`); return; }
      if (await lcall(() => api.post(`/letters/${r.id}/submit`, { signataire_id: sig.id }), `Envoyé en signature à ${sig.nom}`)) nav(`/courriers/${r.id}`);
      return;
    }
    const target = perms.includes("validate") ? "VALIDÉ" : "À VALIDER";
    if (target === "À VALIDER" && r.statut === "À VALIDER") { toast.success("Déjà soumis à validation"); return; }
    if (await lcall(() => api.post(`/letters/${r.id}/status`, { statut: target }), target === "VALIDÉ" ? "Courrier validé" : "Soumis à validation")) nav(`/courriers/${r.id}`);
  };
  const openPreview = async () => { setPages(null); setPreview(true); setPages(await output.generate()); };
  const applyTemplate = (t) => {
    const html = fillVars(t.contenu_html, l, cfg, true);
    const hasText = editor.getText().trim().length > 120;
    if (hasText && !window.confirm("Remplacer le contenu actuel par le modèle ?\n(Annuler = insérer le modèle à la position du curseur)")) editor.chain().focus().insertContent(html).run();
    else editor.commands.setContent(html, { emitUpdate: true });
    set({ template_id: t.id, ...(l.objet ? {} : { objet: fillVars(t.objet, l, cfg, true).replace(/&amp;/g, "&") }), ...(t.type ? { type: t.type } : {}) });
    toast.success(`Modèle « ${t.nom} » appliqué`);
  };
  const onReady = (ed) => {
    setEditor(ed);
    ed.on("selectionUpdate", ({ editor: e }) => {
      const { from, to } = e.state.selection;
      setSel(to > from ? { from, to, text: e.state.doc.textBetween(from, to, " ") } : null);
    });
  };
  const toAi = () => aiRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div data-testid="letter-editor-page">
      <ActionBar onSave={() => save()} onDraft={() => save("BROUILLON")} onPreview={openPreview} onAi={toAi} onPdf={output.download} onPrint={output.print}
        onValidate={validate} saving={saving} busy={output.busy} canValidate={perms.includes("validate")} statut={l.statut} reference={l.reference || refPreview} />
      <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)_300px] lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-white p-3 h-fit" data-testid="zone-info"><LetterInfoForm l={l} set={set} cfg={cfg} refPreview={refPreview} /></aside>
        <main className="min-w-0" data-testid="zone-editor">
          <RichEditor value={l.contenu_html} onChange={(h) => set({ contenu_html: h })} onReady={onReady} onAiSelection={toAi} />
          <p className="text-[11px] text-slate-500 mt-1">L'en-tête, le bloc destinataire, l'objet, la signature et le pied de page officiels sont ajoutés automatiquement à l'aperçu et au PDF.</p>
        </main>
        <aside className="space-y-3 lg:col-span-2 xl:col-span-1" data-testid="zone-tools">
          {perms.includes("ai") && <div ref={aiRef}><AiPanel editor={editor} letter={l} selection={sel} onObjet={(o) => set({ objet: o })} /></div>}
          <div className="rounded-xl border bg-white p-3 space-y-3">
            <TemplatePicker onPick={applyTemplate} />
            <div>
              <p className="text-sm font-semibold mb-1">Variables dynamiques</p>
              <div className="flex flex-wrap gap-1">
                {VARIABLES.map(([k, lab]) => <button key={k} type="button" title={lab} onClick={() => editor?.chain().focus().insertContent(`{{${k}}}`).run()} className="rounded bg-brand-50 text-brand-800 px-1.5 py-0.5 text-[10px] font-mono hover:bg-brand-100" data-testid={`var-${k}`}>{`{{${k}}}`}</button>)}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Remplacées automatiquement à l'aperçu et dans le PDF.</p>
            </div>
            <Attachments letter={l} canEdit={perms.includes("edit")} onChange={() => api.get(`/letters/${l.id}`).then((r) => set({ attachments: r.data.attachments }))} />
          </div>
        </aside>
      </div>
      <PreviewDialog open={preview} onOpenChange={setPreview} pages={pages} />
      {output.node}
    </div>
  );
}
